"use client";

import { getFirstThumbnail, getRequiredYarns, type RGProduct } from "@raumagarn/formatters";
import { useSetQuantity } from "@trydig/centra/client";
import { useRef } from "react";
import { CartImage } from "~/components/ui/CartItem/CartImage";
import { CartNotification } from "~/components/ui/CartNotification/CartNotification";
import { QuantityCounter } from "~/components/ui/QuantityCounter";
import { Link } from "~/i18n/routing";
import type { CartItem as CartItemType } from "~/lib/hooks/useCart";
import { formatPrice } from "~/lib/utils/price";
import DeleteSvg from "~/public/icons/24px/Delete.svg";
import styles from "./CartItem.module.css";

export interface CartItemProps {
	item: CartItemType;
	groupItemsIds?: string[];
	variant?: "default" | "simple";
}

export const CartItem = ({ item, groupItemsIds, variant = "default" }: CartItemProps) => {
	const linkRef = useRef<HTMLAnchorElement>(null!);
	const setQuantity = useSetQuantity({
		optimistic: true,
	});

	if (!item.product) return null;

	const product = item.product as RGProduct;

	const isRecipe = product.pr_product_type_type === "recipe";

	const isOnSale =
		item.priceEachAsNumber && item.priceEachBeforeDiscount
			? item.priceEachAsNumber! < item.priceEachBeforeDiscountAsNumber!
			: false;

	const removeItem = () => {
		if (!item.line) return;

		setQuantity.mutate({
			line: item.line,
			quantity: 0,
		});
	};

	const removeAllGroupItems = () => {
		if (groupItemsIds) {
			groupItemsIds.forEach((id) => {
				setQuantity.mutate({
					line: id,
					quantity: 0,
				});
			});
		}
	};

	/**
	 * Custom logic here to handle click on the cart item for everything
	 * except the buttons and other links iniside
	 */
	const handleCartItemClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLElement;
		if (
			target.closest("a") ||
			target.closest("button") ||
			target.closest(`.${styles.counter}`) ||
			target.tagName === "BUTTON"
		)
			return;

		linkRef.current?.click();
	};

	const requiredYarns = getRequiredYarns(item);

	return (
		<div className={styles.wrapper}>
			{/** biome-ignore lint/a11y/noStaticElementInteractions: <explanation> */}
			{/** biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
			<div className={styles["cart-item"]} onClick={handleCartItemClick}>
				<CartImage
					image={getFirstThumbnail(item) || ""}
					alt=""
					type={product.pr_product_type_type}
				/>

				<div className={styles.details}>
					<div className={styles["first-row"]}>
						<div className={styles["name-container"]}>
							<Link ref={linkRef} href={product.uri || "#"} className={styles.name}>
								{product.name}
							</Link>

							<p className={styles.variant}>{product.variantName}</p>
						</div>

						{variant !== "simple" && (
							// biome-ignore lint/a11y/useButtonType: <explanation>
							<button onClick={isRecipe ? removeAllGroupItems : removeItem}>
								<DeleteSvg />
							</button>
						)}
					</div>

					<div className={styles["last-row"]}>
						<p>
							{formatPrice(item.priceEach)}{" "}
							{isOnSale && (
								<span className={styles.discountedPrice}>
									{formatPrice(item.priceEachBeforeDiscount)}
								</span>
							)}
						</p>

						{variant !== "simple" ? (
							<div className={styles.counter}>
								<QuantityCounter quantity={item.quantity} line={item.line} />
							</div>
						) : (
							<p className={styles.variant}>{item.quantity} stk</p>
						)}
					</div>
				</div>
			</div>

			{((isRecipe && requiredYarns > 0) ||
				(product.pr_product_type_type === "booklet" && requiredYarns > 0)) && (
				<CartNotification
					type="info"
					message={`Når du kjøper denne oppskriften, må du også kjøpe minst ${requiredYarns ?? 4} valgfrie
            garnnøster.`}
				/>
			)}
		</div>
	);
};
